<script setup lang="ts">
import { useFieldTexts, useValidationRules } from '@/composables';
import {
    useAttrs, ref, onMounted,
} from 'vue';
import type { VCheckbox } from 'vuetify/components';

type PropsType = {
    modelValue?: boolean;
    label?: string;
    color?: string;
    baseColor?: string;
    density?: VCheckbox['$props']['density'];
    hideDetails?: boolean | 'auto';
    indeterminate?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    trueValue?: unknown;
    falseValue?: unknown;
    ripple?: boolean;
    isLabelTranslated?: boolean;
    showRequiredVisual?: boolean;
    class?: string;
    name?: string;
    dataType?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    modelValue: false,
    color: 'primary',
    baseColor: 'grey-700',
    density: 'comfortable',
    hideDetails: true,
    indeterminate: false,
    disabled: false,
    readonly: false,
    ripple: true,
    isLabelTranslated: true,
    showRequiredVisual: true,
    dataType: 'boolean',
});

const emit = defineEmits<{
    'update:modelValue': [value: boolean | unknown];
    'update:indeterminate': [value: boolean];
    'update:focused': [value: boolean];
}>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const { getLabel } = useFieldTexts({
    label: props.label,
    isTranslated: props.isLabelTranslated,
});

const { rules, isRequired } = useValidationRules(attrs);

const checkboxRef = ref<VCheckbox | null>(null);

onMounted(() => {
    const input = checkboxRef.value?.$el?.querySelector('input[type="checkbox"]');
    if (input) {
        input.dataset.type = props.dataType;
    }
});
</script>

<template>
    <VCheckbox
        ref="checkboxRef"
        :model-value="modelValue"
        :label="getLabel"
        :color="color"
        :base-color="baseColor"
        :density="density"
        :hide-details="hideDetails"
        :indeterminate="indeterminate"
        :disabled="disabled"
        :readonly="readonly"
        :true-value="trueValue"
        :false-value="falseValue"
        :ripple="ripple"
        :name="name"
        :class="[props.class, $attrs.class, { required: isRequired && showRequiredVisual }]"
        v-bind="{
            ...$attrs,
            rules: rules,
        }"
        @update:indeterminate="emit('update:indeterminate', $event)"
        @update:focused="emit('update:focused', $event)"
        @update:model-value="emit('update:modelValue', $event)"
    >
        <template
            v-if="$slots.prepend"
            #prepend
        >
            <slot name="prepend" />
        </template>

        <template
            v-if="$slots.append"
            #append
        >
            <slot name="append" />
        </template>

        <template
            v-if="$slots.label"
            #label
        >
            <slot name="label" />
        </template>

        <template
            v-if="$slots.message"
            #message="{ message }"
        >
            <slot
                name="message"
                :message="message"
            />
        </template>

        <template
            v-if="$slots.details"
            #details
        >
            <slot name="details" />
        </template>
    </VCheckbox>
</template>
